import { useCallback, useState, type FunctionComponent } from 'react';

import { forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import type { SecureString } from '@trello/privacy';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_SECURE_STRING,
} from '@trello/privacy';

import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { TextFieldDescription } from './PowerUpApiKeyPage/components/TextFieldDescription';
import { useCreateOAuth2ApplicationMutation } from './CreateOAuth2ApplicationMutation.generated';
import { usePluginBasicInformationQuery } from './PluginBasicInformationQuery.generated';

import styles from './PowerUpOAuth2Page.less';

const format = forTemplate('app_management');

export const PowerUpOAuth2Page: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const {
    data: pluginBasicInformation,
    error: errorPluginBasicInformation,
    loading: loadingPluginBasicInformation,
  } = usePluginBasicInformationQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });
  const [createOAuth2Application, { loading: createOAuth2ApplicationLoading }] =
    useCreateOAuth2ApplicationMutation();
  const [oauth2Id, setOauth2Id] = useState<string>('');
  const [oauth2Secret, setOauth2Secret] =
    useState<SecureString>(EMPTY_SECURE_STRING);

  const { dialogProps, show, hide } = useDialog();

  const createNewApplication = useCallback(async () => {
    try {
      if (
        pluginBasicInformation?.plugin?.author &&
        pluginBasicInformation?.plugin?.listings &&
        pluginBasicInformation?.plugin?.email &&
        pluginBasicInformation?.plugin?.supportEmail
      ) {
        //TODO: Get the listing and name for the current locale
        const pluginName = pluginBasicInformation.plugin.listings.find(
          (listing) => listing.locale === 'en-US',
        )?.name;
        const { data: oauth2Response } = await createOAuth2Application({
          variables: {
            author: pluginBasicInformation?.plugin?.author,
            appName: pluginName || '',
            email: pluginBasicInformation?.plugin?.email,
            supportEmail: pluginBasicInformation?.plugin?.supportEmail,
          },
        });
        if (oauth2Response?.createOAuth2Application) {
          setOauth2Id(oauth2Response.createOAuth2Application.id);
          setOauth2Secret(oauth2Response.createOAuth2Application.secret);
          show();
        }
      } else {
        console.error('Missing required fields');
      }
    } catch (error) {
      console.error(error);
    }
  }, [createOAuth2Application, pluginBasicInformation, show]);

  if (loadingPluginBasicInformation) {
    return <LoadingSpinner />;
  }

  if (errorPluginBasicInformation) {
    return <div>{format('something-went-wrong')}</div>;
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>OAuth 2.0</h1>
      </div>
      <div className={styles.createNewApplication}>
        <Button
          isLoading={createOAuth2ApplicationLoading}
          onClick={createNewApplication}
        >
          Create a new OAuth 2.0 application
        </Button>
      </div>
      <Dialog
        {...dialogProps}
        show={show}
        hide={hide}
        size="large"
        alignment={'center'}
      >
        <div className={styles.dialog}>
          <div className={styles.modalHeader}>
            <h1>OAuth 2.0 Application</h1>
          </div>
          <p>
            Please copy your OAuth2.0 Client Id and Secret, then paste them
            somewhere safe because we do not currently save this data in Trello.
            They are necessary to request an access token in the OAuth2.0 flow.
          </p>
          <TextFieldDescription
            label="Client Id"
            value={oauth2Id}
            description=""
          />
          <TextFieldDescription
            label="Client Secret"
            value={dangerouslyConvertPrivacyString(oauth2Secret)}
            description=""
          />
          <div className={styles.modalFooter}>
            <Button onClick={hide}>Close</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
